/**
 * Per-DATE actuals for the unoptimised (current) side of the Weekly Service
 * Schedule — THE one owner of the like-for-like date mapping.
 *
 * Chris, 2026-09-02: "It has the same passenger count for each day of the week
 * and every Saturday and every Sunday. There is no way the exact same no of
 * passengers are travelling each day... This needs to have the right no of
 * passengers for the day." The old cells stamped a month × day-class AVERAGE
 * onto every date. These are the REAL per-day sales instead.
 *
 * The rule: a design-season date maps LIKE-FOR-LIKE onto the sales-basis season
 * chosen by law 6 (chooseSalesBasis, via sourceTierFor): within one month and
 * one weekday class (Sat / Sun / weekday — derived from the date itself, so a
 * future season needs no sales calendar), the nth basis date answers the nth
 * design date. The 2nd Saturday of September is valued at what the 2nd Saturday
 * of the basis September actually took. A design date beyond the basis count
 * has NO like-for-like day — it is named in `unmatched` and the caller falls
 * back to the class average, labelled, never silently blended (law 8).
 *
 * A date's actual = timed slot sales (agg.ResearchSlotDaily, summed over the
 * day's departures) + untimed walk-ups (agg.ResearchUntimedDaily, db/79).
 */
import type { SlotDailyRow, UntimedDailyRow } from './types'

export interface DateActual {
  pax: number
  net: number
  /** Admissions sold on TIMED departures that day (the standard-services
   *  sessions). Zero means no timetabled daily service sold a seat: the
   *  railway ran a gala/event or nothing at all — the day's untimed till
   *  admissions are event traffic, not daily-services demand. THE test for
   *  "no standard services ran" (perDateValue.ts::isNoStandardServiceDay). */
  timedPax: number
  /** The basis-season date this design date is valued from. */
  basisDate: string
  /** The basis day's TIMED slot rows — so the MODELLED timetable can be valued
   *  against that day's actual demand, slot by slot (Chris, 2026-09-02: "your
   *  modelled season uses the SALES as they were — like for like, day for day"). */
  slotRows: SlotDailyRow[]
  /** The basis day's untimed walk-up component (already inside pax/net). */
  untimedPax: number
  untimedNet: number
}

export interface DateActualsResult {
  /** design ISO date -> the like-for-like basis day's real sales. */
  byDate: Record<string, DateActual>
  /** Design dates with no like-for-like basis day (basis ran fewer of that
   *  weekday class that month) — the caller shows the class average and says so. */
  unmatched: string[]
}

/** Sat / Sun / Wkdy from the date itself — always derivable, past or future. */
export const coarseClassOf = (iso: string): 'Sat' | 'Sun' | 'Wkdy' => {
  const dow = new Date(`${iso}T00:00:00Z`).getUTCDay()
  return dow === 6 ? 'Sat' : dow === 0 ? 'Sun' : 'Wkdy'
}

/**
 * The nth-of-class pairing itself: within each weekday class, the nth source
 * date answers the nth design date. Shared by the sales actuals and the
 * Pullman programme mapping so "like-for-like day" means ONE thing.
 */
export function pairLikeDates(designDates: string[], sourceDates: string[]): { byDate: Record<string, string>; unmatched: string[] } {
  const byClass = (dates: string[]): Record<'Sat' | 'Sun' | 'Wkdy', string[]> => {
    const out: Record<'Sat' | 'Sun' | 'Wkdy', string[]> = { Sat: [], Sun: [], Wkdy: [] }
    for (const d of [...new Set(dates)].sort()) out[coarseClassOf(d)].push(d)
    return out
  }
  const src = byClass(sourceDates)
  const design = byClass(designDates)
  const byDate: Record<string, string> = {}
  const unmatched: string[] = []
  for (const cls of ['Sat', 'Sun', 'Wkdy'] as const) {
    design[cls].forEach((d, i) => {
      const s = src[cls][i]
      if (s) byDate[d] = s
      else unmatched.push(d)
    })
  }
  return { byDate, unmatched }
}

/**
 * Map one design month's running dates onto the basis season's real days.
 * `slots`/`untimedDaily` are the CHOSEN basis tier's rows (sourceTierFor owns
 * which season that is); `monthNo` filters them to the like month.
 */
export function mapDateActuals(args: {
  designDates: string[]
  monthNo: number
  slots: SlotDailyRow[]
  untimedDaily: UntimedDailyRow[]
}): DateActualsResult {
  const { designDates, monthNo, slots, untimedDaily } = args

  // Basis day totals: timed slot sales summed per date, walk-ups added.
  const basisDays = new Map<string, { pax: number; net: number; timedPax: number; untimedPax: number; untimedNet: number; slotRows: SlotDailyRow[] }>()
  for (const r of slots) {
    if (r.month_no !== monthNo) continue
    const d = basisDays.get(r.actual_date) ?? { pax: 0, net: 0, timedPax: 0, untimedPax: 0, untimedNet: 0, slotRows: [] }
    d.pax += r.pax
    d.net += r.net
    d.timedPax += r.pax
    d.slotRows.push(r)
    basisDays.set(r.actual_date, d)
  }
  for (const u of untimedDaily) {
    if (Number(u.actual_date.slice(5, 7)) !== monthNo) continue
    const d = basisDays.get(u.actual_date) ?? { pax: 0, net: 0, timedPax: 0, untimedPax: 0, untimedNet: 0, slotRows: [] }
    // A day with till admissions but NO timed sales is still a basis day — it
    // pairs like-for-like — but it is a day the railway ran no standard
    // services (a gala, an event, or nothing): timedPax stays 0 and every
    // reader treats it as such (isNoStandardServiceDay). Its pax/net are kept
    // so the screen can name what the tills took that day.
    d.pax += u.pax
    d.net += u.net
    d.untimedPax += u.pax
    d.untimedNet += u.net
    basisDays.set(u.actual_date, d)
  }

  // nth-of-class pairing (shared with the Pullman programme mapping).
  const pairing = pairLikeDates(designDates, [...basisDays.keys()])
  const byDate: Record<string, DateActual> = {}
  for (const [designDate, basisDate] of Object.entries(pairing.byDate)) {
    const v = basisDays.get(basisDate)!
    byDate[designDate] = { pax: v.pax, net: v.net, timedPax: v.timedPax, basisDate, slotRows: v.slotRows, untimedPax: v.untimedPax, untimedNet: v.untimedNet }
  }
  return { byDate, unmatched: pairing.unmatched }
}
